import { screenToWorld, worldToScreen, type ViewState } from '../camera/cameraMath';
import type { IsoProjection } from './iso';
import type { GridPoint, ScreenPoint, WorldPoint } from './types';

/**
 * The single place where the three coordinate spaces meet:
 *
 *   screen (CSS px on the canvas) ⇄ world (px before camera) ⇄ tile (grid index)
 *
 *  - screen ⇄ world depends on the camera (scroll + zoom): cameraMath.ts
 *  - world  ⇄ tile  depends only on tile size: IsoProjection
 *
 * Everything that needs "which tile is under this point" (picking, hover,
 * future placement and unit orders) goes through this class, so the answer
 * is identical everywhere. Pure and engine-free; the view is read lazily so
 * conversions always use the camera as it is right now.
 */
export class CoordinateSystem {
  constructor(
    readonly iso: IsoProjection,
    readonly mapWidth: number,
    readonly mapHeight: number,
    private readonly view: () => ViewState,
  ) {}

  // ---- screen ⇄ world
  screenToWorld(p: ScreenPoint): WorldPoint {
    return screenToWorld(this.view(), p.x, p.y);
  }

  worldToScreen(p: WorldPoint): ScreenPoint {
    return worldToScreen(this.view(), p.x, p.y);
  }

  // ---- world ⇄ tile
  /** Tile containing a world point; may be outside the map. */
  worldToTile(p: WorldPoint): GridPoint {
    return this.iso.worldToGrid(p.x, p.y);
  }

  /** World-space centre of a tile. */
  tileToWorld(t: GridPoint): WorldPoint {
    return this.iso.gridToWorld(t.x, t.y);
  }

  // ---- screen ⇄ tile
  /** Tile under a screen point, ignoring map bounds. */
  screenToTileUnbounded(p: ScreenPoint): GridPoint {
    return this.worldToTile(this.screenToWorld(p));
  }

  /** Tile under a screen point, or null when the point is off the map. */
  screenToTile(p: ScreenPoint): GridPoint | null {
    const t = this.screenToTileUnbounded(p);
    return this.inBounds(t) ? t : null;
  }

  /** Screen position of a tile's centre. */
  tileToScreen(t: GridPoint): ScreenPoint {
    return this.worldToScreen(this.tileToWorld(t));
  }

  /** Screen positions of a tile's diamond corners: top, right, bottom, left. */
  tileCornersOnScreen(t: GridPoint): ScreenPoint[] {
    return this.iso.tileCorners(t.x, t.y).map((c) => this.worldToScreen(c));
  }

  inBounds(t: GridPoint): boolean {
    return Number.isInteger(t.x) && Number.isInteger(t.y) && t.x >= 0 && t.y >= 0 && t.x < this.mapWidth && t.y < this.mapHeight;
  }
}
